import React, { useState } from 'react';
import { getCloudFunctionUrl } from '../utils/cloudFunctions';
import './TurboAiSetup.css';

const TurboAiSetup = ({ isOpen, onClose, userId }) => {
  const [inputText, setInputText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Input, 2: Generating, 3: Preview
  const [generatedSettings, setGeneratedSettings] = useState({
    brandGuidelines: '',
    emailGuidelines: '',
    cleanupGuidelines: '',
    replyAgentGuidelines: '',
    safeGuardSettings: {
      emailDos: '',
      emailDonts: '',
      targetAudience: '',
      veryImportant: '',
      minimumRating: 6,
      enabled: true
    }
  });

  const generateAiSettings = async () => {
    if (!inputText.trim() || !userId) return;

    setIsGenerating(true);
    setCurrentStep(2);
    
    try {
      const response = await fetch(getCloudFunctionUrl('turboAiAnalyze'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extractedText: inputText.trim(),
          userId: userId
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.settings) {
        // Validierung und Bereinigung der empfangenen Settings
        const cleanedSettings = {
          brandGuidelines: typeof data.settings.brandGuidelines === 'string' ? data.settings.brandGuidelines : '',
          emailGuidelines: typeof data.settings.emailGuidelines === 'string' ? data.settings.emailGuidelines : '',
          cleanupGuidelines: typeof data.settings.cleanupGuidelines === 'string' ? data.settings.cleanupGuidelines : '',
          replyAgentGuidelines: typeof data.settings.replyAgentGuidelines === 'string' ? data.settings.replyAgentGuidelines : '',
          safeGuardSettings: {
            emailDos: typeof data.settings.safeGuardSettings?.emailDos === 'string' ? data.settings.safeGuardSettings.emailDos : '',
            emailDonts: typeof data.settings.safeGuardSettings?.emailDonts === 'string' ? data.settings.safeGuardSettings.emailDonts : '',
            targetAudience: typeof data.settings.safeGuardSettings?.targetAudience === 'string' ? data.settings.safeGuardSettings.targetAudience : '',
            veryImportant: typeof data.settings.safeGuardSettings?.veryImportant === 'string' ? data.settings.safeGuardSettings.veryImportant : '',
            minimumRating: typeof data.settings.safeGuardSettings?.minimumRating === 'number' ? data.settings.safeGuardSettings.minimumRating : 6,
            enabled: typeof data.settings.safeGuardSettings?.enabled === 'boolean' ? data.settings.safeGuardSettings.enabled : true
          }
        };
        
        // Überprüfe, ob alle erforderlichen Felder vorhanden sind
        const requiredFields = ['brandGuidelines', 'emailGuidelines', 'cleanupGuidelines', 'replyAgentGuidelines'];
        const missingFields = requiredFields.filter(field => !cleanedSettings[field] || cleanedSettings[field].trim() === '');
        
        // Überprüfe SafeGuard-Settings
        const safeGuardFields = ['emailDos', 'emailDonts', 'targetAudience', 'veryImportant'];
        const missingSafeGuardFields = safeGuardFields.filter(field => !cleanedSettings.safeGuardSettings[field] || cleanedSettings.safeGuardSettings[field].trim() === '');
        
        if (missingFields.length > 0) {
          throw new Error(`Missing or empty fields: ${missingFields.join(', ')}`);
        }
        
        if (missingSafeGuardFields.length > 0) {
          throw new Error(`Missing SafeGuard fields: ${missingSafeGuardFields.join(', ')}`);
        }
        
        console.log('Successfully received AI settings:', Object.keys(cleanedSettings));
        setGeneratedSettings(cleanedSettings);
        setCurrentStep(3);
      } else {
        throw new Error(data.error || 'Failed to generate AI settings');
      }
    } catch (error) {
      console.error('Error generating AI settings:', error);
      alert('Error generating AI settings: ' + error.message);
      setCurrentStep(1);
    } finally {
      setIsGenerating(false);
    }
  };

  const saveSettings = async () => {
    if (!generatedSettings || !userId) return;

    // Validierung vor dem Speichern
    const requiredFields = ['brandGuidelines', 'emailGuidelines', 'cleanupGuidelines', 'replyAgentGuidelines'];
    const missingFields = requiredFields.filter(field => !generatedSettings[field] || generatedSettings[field].trim() === '');
    
    // SafeGuard-Settings Validierung
    const safeGuardFields = ['emailDos', 'emailDonts', 'targetAudience', 'veryImportant'];
    const missingSafeGuardFields = safeGuardFields.filter(field => !generatedSettings.safeGuardSettings[field] || generatedSettings.safeGuardSettings[field].trim() === '');
    
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    if (missingSafeGuardFields.length > 0) {
      alert(`Please fill in all SafeGuard fields: ${missingSafeGuardFields.join(', ')}`);
      return;
    }

    try {
      const response = await fetch(getCloudFunctionUrl('updateAiSettings'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          settings: generatedSettings
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('AI and SafeGuard settings saved successfully! 🎉');
        handleClose();
      } else {
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + error.message);
    }
  };

  const handleClose = () => {
    setInputText('');
    setCurrentStep(1);
    setGeneratedSettings({
      brandGuidelines: '',
      emailGuidelines: '',
      cleanupGuidelines: '',
      replyAgentGuidelines: '',
      safeGuardSettings: {
        emailDos: '',
        emailDonts: '',
        targetAudience: '',
        veryImportant: '',
        minimumRating: 6,
        enabled: true
      }
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="turboAiSetup-overlay">
      <div className="turboAiSetup-modal">
        <div className="turboAiSetup-header">
          <h2>
            <div className="turboAiSetup-header-icon">
              <img src="/img/star.svg" alt="AI Technology" />
            </div>
            Turbo AI Setup
          </h2>
          <button className="turboAiSetup-close" onClick={handleClose}>
            <img src="/img/tag_close.svg" alt="Close" />
          </button>
        </div>

        <div className="turboAiSetup-content">
          {/* Step 1: Text Input */}
          {currentStep === 1 && (
            <div className="turboAiSetup-step">
              <h3>Describe Your Business & Brand</h3>
              <p>Tell us about your business, communication style, and target audience. The more detailed you are, the better your personalized AI settings will be.</p>
              
              <div className="turboAiSetup-text-section">
                <textarea
                  className="turboTextInput"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Describe your business and brand in detail...

Include:
🏢 Your name, role, and company
👥 Target audience (company size, industry, location)
💬 Communication style and tone
🎯 What makes you unique
📋 Key services and expertise

Example:
'I am [Name], [Role] at [Company]. We help [Target Audience] with [Service]. Our clients are [Description] who [Challenges]. Our communication style is [Style Description]...'"
                  maxLength={10000}
                  style={{ 
                    minHeight: '240px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    resize: 'vertical'
                  }}
                />
                
                <div className="turboAiSetup-text-info">
                  <small>
                    Characters: {inputText.length} / 10,000
                    {inputText.length > 150 ? 
                      <span style={{color: '#10b981'}}> ✓ Good length</span> : 
                      <span style={{color: '#f59e0b'}}> (Aim for 150+ characters)</span>
                    }
                  </small>
                </div>
              </div>
              
              <div className="turboAiSetup-actions">
                {inputText.trim().length > 50 ? (
                  <button 
                    className="turboAiSetup-btn-primary"
                    onClick={generateAiSettings}
                    disabled={isGenerating}
                  >
                    <img src="/img/magic-wand.svg" alt="Generate" className="turboAiSetup-btn-icon" />
                    Generate AI Settings
                  </button>
                ) : (
                  <p className="input-hint">
                    💡 Enter at least 50 characters to generate settings
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Generating */}
          {currentStep === 2 && (
            <div className="turboAiSetup-step">
              <div className="turboAiSetup-processing">
                <div className="turboAiSetup-spinner"></div>
                <h3>Creating Your AI Settings...</h3>
                <p>Analyzing your business and generating personalized settings...</p>
              </div>
            </div>
          )}

          {/* Step 3: Preview & Edit */}
          {currentStep === 3 && (
            <div className="turboAiSetup-step">
              <h3>Your Personalized AI Settings</h3>
              <p>Review and edit your AI settings below.</p>
              
              <div className="turboAiSetup-settings-preview">
                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/target.svg" alt="Target" />
                    </div>
                    Brand Guidelines
                  </h4>
                  <textarea 
                    value={generatedSettings.brandGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      brandGuidelines: e.target.value
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/mail_check.svg" alt="Email" />
                    </div>
                    Email Guidelines
                  </h4>
                  <textarea 
                    value={generatedSettings.emailGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      emailGuidelines: e.target.value
                    }))}
                    rows="8"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/binoculars.svg" alt="Filter" />
                    </div>
                    Contact Filtering
                  </h4>
                  <textarea 
                    value={generatedSettings.cleanupGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      cleanupGuidelines: e.target.value
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/reply-message.svg" alt="Reply" />
                    </div>
                    Reply Guidelines
                  </h4>
                  <textarea 
                    value={generatedSettings.replyAgentGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      replyAgentGuidelines: e.target.value
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Email Dos
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.emailDos}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: {
                        ...prev.safeGuardSettings,
                        emailDos: e.target.value
                      }
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Email Don'ts
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.emailDonts}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: {
                        ...prev.safeGuardSettings,
                        emailDonts: e.target.value
                      }
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Target Audience
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.targetAudience}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: {
                        ...prev.safeGuardSettings,
                        targetAudience: e.target.value
                      }
                    }))}
                    rows="4"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Very Important
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.veryImportant}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: {
                        ...prev.safeGuardSettings,
                        veryImportant: e.target.value
                      }
                    }))}
                    rows="4"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Settings
                  </h4>
                  <div className="turboAiSetup-safeGuard-controls">
                    <label className="turboAiSetup-checkbox-label">
                      <input
                        type="checkbox"
                        checked={generatedSettings.safeGuardSettings.enabled}
                        onChange={(e) => setGeneratedSettings(prev => ({
                          ...prev,
                          safeGuardSettings: {
                            ...prev.safeGuardSettings,
                            enabled: e.target.checked
                          }
                        }))}
                      />
                      Enable SafeGuard
                    </label>
                    <label className="turboAiSetup-range-label">
                      Minimum Rating: {generatedSettings.safeGuardSettings.minimumRating}/10
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={generatedSettings.safeGuardSettings.minimumRating}
                        onChange={(e) => setGeneratedSettings(prev => ({
                          ...prev,
                          safeGuardSettings: {
                            ...prev.safeGuardSettings,
                            minimumRating: parseInt(e.target.value)
                          }
                        }))}
                      />
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="turboAiSetup-actions">
                <button 
                  className="turboAiSetup-btn-secondary"
                  onClick={() => setCurrentStep(1)}
                >
                  <img src="/img/refresh-arrow.svg" alt="Back" className="turboAiSetup-btn-icon" />
                  Back to Input
                </button>
                <button 
                  className="turboAiSetup-btn-primary"
                  onClick={saveSettings}
                >
                  <img src="/img/check.svg" alt="Save" className="turboAiSetup-btn-icon" />
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TurboAiSetup; 